import { ApolloQueryResult, useQuery, ApolloError } from '@apollo/client';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import groupBy from 'lodash/groupBy';
import defaultTo from 'lodash/defaultTo';
import kebabCase from 'lodash/kebabCase';
import { Flex, Stack } from '@atlaskit/primitives';
import ToolTip from '@atlaskit/tooltip';
import EditIcon from '@atlaskit/icon/glyph/edit';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { AnalyticsEventSource, trackSearchEvent } from '@atlassian/temp-analytics';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  FormattedAddress,
  NotificationTypes,
  OpenSidebarButton,
  successNotificationBuilder,
  SummaryPanelContainer,
  SummaryPanelRow,
  TabPanel,
  Tabs,
  useFeatureToggle,
  useNotification,
  useRecentlyViewed,
} from '@css-xp/base';
import {
  Maybe,
  CommerceAddress,
  FetchShipToInPpcQuery,
  FetchShipToInPpcQueryVariables,
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { CustomerAddressDetailPageLoader, TableLoader } from '@css-xp/ppc-page-loaders';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  CCP_SEARCH_MAX_LIMIT,
  COMMERCIAL_PRICE_ELIGIBILITY,
  ENABLE_DESIGN_ITERATION_1,
  MAX_ROWS_PER_PAGE,
  PPC_PAGE_TITLE,
  RecentlyViewedEntityEnum,
  refundedTabNameKey,
  routePaths,
  STATSIG_FEATURE_GATES,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import {
  getCompanyNameFromRoute,
  getCompanyNameRoute,
  getEntityName,
  getRouteUrl,
  isCommercialPricingEligibility,
} from '@css-xp/ppc-utils';
import {
  BillingProfileTable,
  CreateBillingProfilePane,
  fetchTransactionAccountForCreateBillingProfile,
  useFetchBillingProfilesForTransactionAccountQuery,
} from '@css-xp/ppc-billing-profile-table';
import { SelectCustomerAddress, SoldToAddressesType } from '@css-xp/ppc-select-customer-address';
import { CommerceAddressSelect } from '@css-xp/ppc-select-customer-address/src/select-customer-address';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import {
  defaultQuotesFilterOptions,
  hiddenTabsQuoteFilterOptions,
  QUOTE_TABLE_COLUMNS,
  QuoteTableColumnsType,
  QuoteTableContainer,
} from '@css-xp/ppc-quote-table';
import { useOpenClose, useTitle } from '@css-xp/ppc-custom-hooks';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { getTaxIdLabels } from '@css-xp/ppc-tax-component';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { COLUMNS } from '@css-xp/ppc-quote-table/src/quote-table';
import { getPartnerServiceDeskUrlForGenericHelp, trackPartnerServiceDeskClick } from '@css-xp/ppc-navigation';
import { IncludedProductsSidebar } from '@css-xp/ppc-subscription-table';
import { CreateBillingProfileModalV2 } from '@css-xp/ppc-billing-profile-table/src/modal/create-billing-profile-modal-v2';
// eslint-disable-next-line workspaces/require-dependency
import { CreateCustomerAndBillingProfileModalV2 } from '@css-xp/ppc-cart-page';
import { CustomerAddressDetailPageActions, SIDEBAR } from './components/customer-address-detail-page-actions';
import { CustomerAddressInvoicesSection } from './components/customer-address-invoices-section';
import { CustomerAddressSummarySection } from './components/customer-address-summary-section';
import { EditAddress } from './components/edit-address';
import { SubscriptionTableSoldToAddress } from './components/subscription-table-sold-to-address';
import { CacheCustomerAddressDetailTabGraphqlQuery } from './components/cache-customer-address-detail-tab-graphql-query';
import { EditCustomerNamePane } from './components/sidebar/edit-customer-name-pane';
import './customer-address-detail-page.scss';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { fetchShipToPartiesForTransactionAccountPPC } from '../../../queries';
import {
  FetchShipToPartiesForTransactionAccountPpcQuery,
  FetchShipToPartiesForTransactionAccountPpcQueryVariables,
} from '@css-xp/ppc-graphtypes';
import { DeactivateSoldToAddressPane } from './components/sidebar/deactivate-ship-to-party-pane';
import { AddNewAddress } from './components/add-new-address';

const allowedSidebars = {
  [SIDEBAR.EDIT_SOLD_TO_ADDRESS]: ShipToPartySidebar,
  [SIDEBAR.CREATE_BILLING_PROFILE]: CreateBillingProfilePane,
  [SIDEBAR.EDIT_CUSTOMER_NAME]: EditCustomerNamePane,
  [SIDEBAR.DEACTIVATE_SOLD_TO_ADDRESS]: DeactivateSoldToAddressPane,
  [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

const TAB_LABELS = {
  SUBSCRIPTIONS: 'Subscriptions',
  BILLING_PROFILES: 'Billing profiles',
  INVOICES: 'Invoices',
  QUOTES: 'Quotes',
  DETAILS: 'Details',
  REFUNDS: 'Refunds',
};

export enum ShipToPriceEligibility {
  COMMERCIAL = 'commercial',
  ACADEMIC = 'academic',
  COMMUNITY = 'community',
}

const buildShipToPartyOptions = (companyName: string) => {
  return {
    pageOptions: {
      cursor: null,
      size: CCP_SEARCH_MAX_LIMIT,
    },
    searchOptions: {
      query: '',
    },
    filterOptions: {
      names: [companyName],
    },
  };
};

const getPricingEligibility = (priceEligibility: Maybe<string> | undefined) =>
  sentenceCase(priceEligibility ?? COMMERCIAL_PRICE_ELIGIBILITY);

const groupByShipToPartyId = (nodes: any) => {
  return groupBy(nodes, (node) => node.shipToParty.id);
};

const renderActiveLinkedBillingProfiles = ({
  billingProfilesCountError,
  billingProfilesCountLoading,
  billingProfileDetails,
  shipToId,
  txaId,
}: {
  billingProfilesCountError: ApolloError | undefined;
  billingProfilesCountLoading: boolean;
  billingProfileDetails: any;
  shipToId: string;
  txaId: string;
}) => {
  if (billingProfilesCountError) return null;
  if (billingProfilesCountLoading) {
    return (
      <SkeletonLoaderWrapper className="billing-profile-text-loader">
        <SkeletonLoader width="100%" marginBottom="0" />
      </SkeletonLoaderWrapper>
    );
  }

  const billingProfileMapping = groupByShipToPartyId(billingProfileDetails.filter((node: any) => node?.isActive));
  const billingProfiles = billingProfileMapping[shipToId] ?? [];
  if (!billingProfiles.length) return 'No active billing profiles linked';
  return (
    <Stack space="space.100">
      {billingProfiles.map((billingProfile) => (
        <Link
          key={billingProfile.id}
          to={getRouteUrl(routePaths.billingProfileDetails, {
            txaId,
            billingProfileId: billingProfile.id,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          {billingProfile.name}
        </Link>
      ))}
    </Stack>
  );
};

export const CustomerAddressDetailPage = () => {
  const [refetchBillingProfiles, setRefetchBillingProfiles] = useState<() => Promise<ApolloQueryResult<unknown>>>();
  const location = useLocation();
  const [selectedTabKey, setSelectedTabKey] = useState<string>(
    kebabCase(defaultTo(location?.hash, TAB_LABELS.SUBSCRIPTIONS))
  );
  const [showPageLoader, setShowPageLoader] = useState(true);
  const [openCreateCustomerAndBillingProfileModal, setIsOpenCreateCustomerAndBillingProfileModal] = useState(false);
  const history = useHistory();
  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
  const { sendSuccessNotification } = useNotification();
  const { updateRecentlyViewed } = useRecentlyViewed();
  let { txaId, addressId, companyName } = useParams<{
    txaId: string;
    addressId: string;
    companyName: string | undefined;
  }>();
  companyName = companyName && getCompanyNameFromRoute(companyName);
  const companyListPageRoute = getRouteUrl(routePaths.customerAddresses, {
    txaId: txaId,
  });
  const [isEnabled] = useFeatureToggle();
  const {
    isOpen: showCreateBillingProfileModal,
    open: openCreateBillingProfileModal,
    close: closeCreateBillingProfileModal,
  } = useOpenClose();

  const handleAddNewAddressClick = () => {
    closeCreateBillingProfileModal();
    setIsOpenCreateCustomerAndBillingProfileModal(true);
  };

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isWildcardBillingProfileSearchEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_WILDCARD_BILLING_PROFILE_SEARCH_HYDRA
  );
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isFutureDatedSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const isDuplicateCustomerNameEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DUPLICATE_CUSTOMER_NAME);

  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);

  const isTransparentBackground = !!companyName;
  const isCustomerContactsEditable = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EDIT_ECC);

  const { data, error, loading, refetch } = useQuery<FetchShipToInPpcQuery, FetchShipToInPpcQueryVariables>(
    fetchShipToInPPC,
    {
      variables: {
        searchParams: {
          soldToAddressId: addressId ?? '',
          transactionAccountId: txaId,
          ...(isSoldToAddressArchivalEnabled && { isActive: true }),
        },
        pageParams: {
          size: CCP_SEARCH_MAX_LIMIT,
        },
      },
      displayName: 'SoldToAddressForAddressIdQuery',
      skip: !txaId || !addressId,
    }
  );

  //@ts-ignore
  const soldToAddress = data?.shipToParty?.nodes?.[0] as SoldToAddress;
  useTitle(soldToAddress?.name ?? companyName ?? '', PPC_PAGE_TITLE.CUSTOMERS);
  /**
   * Recently Viewed Feature starts
   */
  useEffect(() => {
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.CUSTOMER_ADDRESS, txaId),
      metadata: {
        id: addressId,
        name: companyName,
      },
    });
  });
  /**
   * Recently Viewed Feature ends
   */

  const {
    data: soldToPartiesByTransactionAccount,
    loading: soldToPartiesLoading,
    error: soldToPartiesError,
    refetch: refetchSoldToParties,
  } = useQuery<
    FetchShipToPartiesForTransactionAccountPpcQuery,
    FetchShipToPartiesForTransactionAccountPpcQueryVariables
  >(fetchShipToPartiesForTransactionAccountPPC, {
    variables: {
      id: txaId,
      shipToPartyOptions: buildShipToPartyOptions(soldToAddress?.name || companyName),
    },
    displayName: 'SoldToAddressForCompanyNameQuery',
    // we need to fetch the shipToParties even when sold to address is fetched for a single address id
    skip: !txaId || !(companyName || soldToAddress?.name),
    client: ppcBffGraphqlClient,
  });
  const soldToParties = soldToPartiesByTransactionAccount?.transactionAccount;

  const onAddressChange = useCallback(
    (valueObj: CommerceAddressSelect['value'], isCompanyName: CommerceAddressSelect['isCompanyName']) => {
      if (isCompanyName) {
        history.replace(
          getCompanyNameRoute({
            txaId: txaId,
            companyName: valueObj.id ?? '',
          })
        );
        return;
      }
      history.replace(
        getRouteUrl(routePaths.customerAddressDetailsById, {
          txaId: txaId,
          addressId: valueObj.id ?? '',
        })
      );
      trackSearchEvent();
    },
    []
  );

  const soldToAddressIds = useMemo(
    () =>
      addressId
        ? [addressId]
        : soldToParties?.shipToParty?.nodes
            ?.filter((soldToAddress) => !!soldToAddress?.id)
            ?.map((soldToAddress) => soldToAddress?.id as string) ?? [],
    [addressId, soldToParties]
  );

  // Filter for Quote tab
  const quoteFilterOptions = useMemo(() => {
    return {
      ...defaultQuotesFilterOptions,
      or: soldToAddressIds?.map((soldToAddressId) => ({
        key: 'shipToId',
        value: soldToAddressId,
      })),
    };
  }, [soldToAddressIds]);

  const hiddenTabsQuoteTabFilterOptions = useMemo(() => {
    return {
      ...hiddenTabsQuoteFilterOptions,
      or: soldToAddressIds?.map((soldToAddressId) => ({
        key: 'shipToId',
        value: soldToAddressId,
      })),
    };
  }, [soldToAddressIds]);

  // Filter for invoice tab
  const invoiceFilterOptions = useMemo(
    () =>
      soldToAddressIds.map((id) => {
        return { shipToId: id };
      }),
    [soldToAddressIds]
  );

  const {
    data: billingProfilesCountData,
    error: billingProfilesCountError,
    loading: billingProfilesCountLoading,
    refetch: refetchBillingProfilesCount,
  } = useFetchBillingProfilesForTransactionAccountQuery({
    soldToAddressIds: soldToAddressIds,
    transactionAccountId: txaId,
    fetchNodes: true,
    numberOfBillingProfilesToFetch: MAX_ROWS_PER_PAGE,
  });

  const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
    FetchTransactionAccountForCreateBillingProfileQuery,
    FetchTransactionAccountForCreateBillingProfileQueryVariables
  >(fetchTransactionAccountForCreateBillingProfile, { variables: { id: txaId } });

  const billingProfileDetails = billingProfilesCountData?.transactionAccount?.billingProfiles?.nodes;
  const defaultSelectedAddress = soldToAddress as CommerceAddress;
  const customerAddresses = soldToParties?.shipToParty?.nodes as SoldToAddressesType;
  const heading = (soldToAddress?.name || companyName) ?? 'Company';

  const getPageHeading = useCallback(
    (hideHeading = false) => {
      return (
        <div className="page-heading">
          {hideHeading || (
            <ToolTip content={heading} tag="span" testId="page-heading-tooltip">
              <h1>{heading}</h1>
            </ToolTip>
          )}
          {isDesignIteration1Enabled || (
            <SelectCustomerAddress
              onSelect={onAddressChange}
              soldToAddresses={customerAddresses}
              defaultSoldToAddress={defaultSelectedAddress}
              customClassName="customer-address-select"
              isLoading={soldToPartiesLoading}
            />
          )}
        </div>
      );
    },
    [soldToParties, companyName, addressId]
  );

  if ((loading || soldToPartiesLoading) && showPageLoader) {
    if (isDesignIteration1Enabled) {
      return <CustomerAddressDetailPageLoader hideActions={!soldToAddress} />;
    }

    return <LoadingIndicator />;
  }

  if (soldToPartiesError || error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);

    return <GenericError error={error || soldToPartiesError} />;
  }
  const emptySoldToAddress = soldToParties?.shipToParty?.nodes?.length === 0;
  if (emptySoldToAddress) {
    return (
      <ErrorComponents.NotFound
        title="We can’t find the customer you’re looking for"
        enableCustomTitle={true}
        description={
          <p>
            Try searching for the customer from the <Link to={companyListPageRoute}>customer list</Link>. If this keeps
            happening, contact{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={getPartnerServiceDeskUrlForGenericHelp()}
              onClick={trackPartnerServiceDeskClick}
            >
              Partner Service Desk
            </a>
          </p>
        }
      ></ErrorComponents.NotFound>
    );
  }
  const getHiddenColumnsForQuotes = () => {
    if (companyName) {
      // The company name column should not be present when “All sold-to addresses“ option is selected
      return [QUOTE_TABLE_COLUMNS.COMPANY_NAME] as QuoteTableColumnsType[];
    }
    if (addressId) {
      // The company name and sold-to address columns should not be present when a particular sold-to address is selected
      return [QUOTE_TABLE_COLUMNS.COMPANY_NAME, QUOTE_TABLE_COLUMNS.SOLD_TO_ADDRESS] as QuoteTableColumnsType[];
    }

    return [];
  };

  const renderSoldToAddressSummary = (layoutAppearance?: LayoutAppearance) => {
    return soldToAddress ? (
      <section className="ph-50">
        <CustomerAddressSummarySection soldToAddress={soldToAddress} layoutAppearance={layoutAppearance} />
      </section>
    ) : null;
  };

  const renderDetailsSummary = () => {
    // Note: Takes styles from pages/customer-address/src/components/customer-address-summary-section.scss

    const renderCompanyName = () => {
      return (
        <Flex alignItems="center" gap="space.300">
          {heading}
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
            <OpenSidebarButton
              sidebar={SIDEBAR.EDIT_CUSTOMER_NAME}
              payload={{
                transactionAccountId: txaId,
                shipToAddresses: customerAddresses,
                name: heading,
                onComplete: ({ customerName }: { customerName: string }) => {
                  // replacing url to the updated company name & refetching the updated company data
                  history.replace(
                    getCompanyNameRoute({
                      txaId,
                      companyName: customerName,
                    }) + '#details'
                  );
                  refetchSoldToParties({
                    id: txaId,
                    shipToPartyOptions: buildShipToPartyOptions(customerName),
                  });
                },
                isSoldToAddressArchivalEnabled,
                isDuplicateCustomerNameEnabled,
              }}
              appearance="subtle"
              testId="edit-customer-name-button"
              label="Edit customer name"
              spacing="compact"
              iconBefore={
                <ToolTip testId="edit-name-tooltip" content="Edit name">
                  <EditIcon label="Edit" size="medium" />
                </ToolTip>
              }
            />
          </AdvocateAccessWall>
        </Flex>
      );
    };

    const getSoldToAddress = (shipToAddress: CommerceAddress) => {
      return (
        <Flex direction="column" gap="space.100">
          <Flex alignItems="center">
            <FormattedAddress
              addressLine1={shipToAddress?.addressLine1}
              addressLine2={shipToAddress?.addressLine2}
              city={shipToAddress?.city}
              country={shipToAddress?.country}
              postcode={shipToAddress?.postcode}
              state={shipToAddress?.state}
              icon={
                <span className="ml-24">
                  <EditAddress
                    transactionAccountId={txaId}
                    refetch={refetchSoldToParties}
                    shipToAddress={shipToAddress}
                    showDeactivateOption={isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS)}
                    isNonCommercialPricingEligibility={
                      !isCommercialPricingEligibility(getPricingEligibility(shipToAddress?.priceEligibility))
                    }
                    isPrimaryContactsEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3)}
                    onCompleteDeactivation={() => {
                      if (!shipToAddress?.name || !customerAddresses) return;
                      const companyName = shipToAddress.name;
                      const remainingActiveSoldToAddressesCount = customerAddresses.length - 1;
                      if (remainingActiveSoldToAddressesCount <= 0) {
                        history.replace(
                          getCompanyNameRoute({
                            txaId,
                            companyName: '',
                          })
                        );
                      } else {
                        history.replace(
                          getCompanyNameRoute({
                            txaId,
                            companyName,
                          }) + '#details'
                        );
                        refetchSoldToParties({
                          id: txaId,
                          shipToPartyOptions: buildShipToPartyOptions(companyName),
                        });
                      }
                    }}
                  />
                </span>
              }
              hasLineBreak={false}
            />
          </Flex>
        </Flex>
      );
    };

    if (!showPageLoader && soldToPartiesLoading) {
      return (
        <div className="mt-20">
          <TableLoader hideSearchBar hideFilters hideActions />
        </div>
      );
    }

    return (
      <div>
        <SummaryPanelContainer title="Customer details" version={2}>
          <SummaryPanelRow label="Company" value={renderCompanyName()} useWrapper />
        </SummaryPanelContainer>
        <SummaryPanelContainer
          title="Sold-to addresses"
          version={2}
          elemAfter={
            <AdvocateAccessWall
              accessType={ADVOCATE_ACCESS_TYPE.CREATE}
              resource={ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS}
            >
              <AddNewAddress companyName={companyName} transactionAccountId={txaId} refetch={refetchSoldToParties} />
            </AdvocateAccessWall>
          }
        >
          <Stack space="space.600">
            {customerAddresses?.map((shipToAddress, idx) => {
              const taxIdLabels = getTaxIdLabels(shipToAddress);
              const shipToId = shipToAddress?.id ?? '';

              return (
                <div key={shipToAddress.id}>
                  <SummaryPanelRow label={`Address #${idx + 1}`} value={getSoldToAddress(shipToAddress)} useWrapper />
                  {taxIdLabels &&
                    taxIdLabels?.map((taxIdLabel) => (
                      <SummaryPanelRow
                        key={`tax-id-label-${taxIdLabel}`}
                        label={taxIdLabel.label}
                        value={taxIdLabel.value}
                        useWrapper
                      />
                    ))}
                  <SummaryPanelRow
                    label="Pricing eligibility"
                    value={getPricingEligibility(shipToAddress?.priceEligibility)}
                    useWrapper
                  />
                  <SummaryPanelRow
                    label="Active linked billing profiles"
                    value={renderActiveLinkedBillingProfiles({
                      billingProfilesCountError,
                      billingProfilesCountLoading,
                      billingProfileDetails,
                      shipToId,
                      txaId,
                    })}
                    useWrapper
                  />
                </div>
              );
            })}
          </Stack>
        </SummaryPanelContainer>
      </div>
    );
  };

  const renderAppPage = () => {
    const quoteTableColumns = [
      COLUMNS.QUOTE_NUMBER,
      COLUMNS.STATUS,
      COLUMNS.QUOTE_TOTAL,
      COLUMNS.PARTNER_MARGIN,
      COLUMNS.BILLING_PROFILE,
      COLUMNS.CREATED,
      COLUMNS.EXPIRES,
      COLUMNS.CUSTOMER,
      COLUMNS.SOLD_TO_ADDRESS,
      COLUMNS.CREATED_BY,
      COLUMNS.UPDATED_BY,
      COLUMNS.CUSTOMER_CONTACTS,
      COLUMNS.QUOTE_CONTACT,
      COLUMNS.PO_NUMBER,
      COLUMNS.LAST_UPDATED,
    ];

    const handleTabSelect = (tabLabel: string) => {
      const tabKey = kebabCase(tabLabel);
      setSelectedTabKey(tabKey);
      history.push({
        hash: tabKey,
      });
      if (tabKey === kebabCase(TAB_LABELS.DETAILS)) {
        setShowPageLoader(false);
        refetchSoldToParties();
        refetchBillingProfilesCount();
      }
    };

    return (
      <AppPage allowedSidebars={allowedSidebars} className="app-page-v2 customer-address-detail-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_CUSTOMER_ADDRESS_DETAIL_SCREEN_BY_COMPANY,
            attributes: {},
          }}
        />
        <AppContent>
          {!isDesignIteration1Enabled && renderSoldToAddressSummary()}
          {isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRAPHQL_CACHING) && (
            <CacheCustomerAddressDetailTabGraphqlQuery
              soldToAddressIds={soldToAddressIds}
              transactionAccountId={txaId}
              quoteFilterOptions={isDesignIteration1Enabled ? hiddenTabsQuoteTabFilterOptions : quoteFilterOptions}
              invoiceFilterOptions={invoiceFilterOptions}
            />
          )}
          {isDesignIteration1Enabled && (
            <CreateBillingProfileModalV2
              isOpen={showCreateBillingProfileModal}
              transactionAccountId={txaId}
              onClose={closeCreateBillingProfileModal}
              customerAddresses={customerAddresses as CommerceAddress[]}
              existingBillingProfileDetails={billingProfileDetails}
              refetchBillingProfiles={refetchBillingProfiles}
              handleAddNewAddressClick={handleAddNewAddressClick}
              isCustomerContactsEditable={isCustomerContactsEditable}
            />
          )}
          {isDesignIteration1Enabled && openCreateCustomerAndBillingProfileModal && (
            <CreateCustomerAndBillingProfileModalV2
              isOpen={openCreateCustomerAndBillingProfileModal}
              transactionAccountId={txaId}
              onClose={() => setIsOpenCreateCustomerAndBillingProfileModal(false)}
              onSubmit={() => {
                if (refetchBillingProfiles) {
                  refetchBillingProfiles();
                }
                //trigger success notification
                const successMessage = successNotificationBuilder()
                  .isVersion(NotificationTypes.FLAG)
                  .withId('create-invoice-group-modal-success')
                  .withHeader('Billing profile created')
                  .withMessage('Refresh the page or check back in some time.')
                  .withDismissAfter(5000)
                  .build();
                sendSuccessNotification(successMessage);
              }}
              transactionAccountData={transactionAccountData}
              transactionAccountDataLoading={transactionAccountDataLoading}
              customerName={companyName}
              isAddNewAddressFlow={true}
              modalHeading="Add billing profile"
              isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
            />
          )}
          <Tabs
            testId="customer-address-details-page-tabs"
            onTabSelect={handleTabSelect}
            selectedTabKey={kebabCase(selectedTabKey)}
          >
            <TabPanel label={TAB_LABELS.SUBSCRIPTIONS}>
              <SubscriptionTableSoldToAddress
                transactionAccountId={txaId}
                soldToAddressIds={soldToAddressIds}
                label={TAB_LABELS.SUBSCRIPTIONS}
                {...(isDesignIteration1Enabled && { shouldHideTabs: true, shouldHideCustomiseView: true })}
                isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
                isSupportForExternallyBilledEnabled={true}
                isTWCPhase1Enabled={isTWCPhase1Enabled}
                isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
              />
            </TabPanel>
            <TabPanel label={TAB_LABELS.QUOTES}>
              <QuoteTableContainer
                transactionAccountId={txaId}
                filterOptions={
                  isDesignIteration1Enabled && !isDefaultFiltersEnabled
                    ? hiddenTabsQuoteTabFilterOptions
                    : quoteFilterOptions
                }
                label={TAB_LABELS.QUOTES}
                hiddenColumns={getHiddenColumnsForQuotes() as QuoteTableColumnsType[]}
                {...(isDesignIteration1Enabled && { columnsToShow: quoteTableColumns })}
              />
            </TabPanel>
            <TabPanel label={TAB_LABELS.BILLING_PROFILES}>
              <BillingProfileTable
                transactionAccountId={txaId}
                soldToAddressIds={soldToAddressIds}
                label={isDesignIteration1Enabled ? '' : TAB_LABELS.BILLING_PROFILES}
                customerAddresses={customerAddresses}
                defaultSelectedAddress={defaultSelectedAddress}
                isWildcardSearchEnabled={isWildcardBillingProfileSearchEnabled}
                {...(isDesignIteration1Enabled && {
                  onAddBillingProfile: openCreateBillingProfileModal,
                })}
                setRefetchBillingProfiles={setRefetchBillingProfiles}
              />
            </TabPanel>
            <TabPanel label={TAB_LABELS.INVOICES}>
              <CustomerAddressInvoicesSection
                transactionAccountId={txaId}
                invoiceFilterOptions={invoiceFilterOptions}
                label={TAB_LABELS.INVOICES}
                {...(isDesignIteration1Enabled && {
                  shouldHideCustomiseView: true,
                })}
              />
            </TabPanel>
            {isDesignIteration1Enabled && (
              <TabPanel label={TAB_LABELS.REFUNDS}>
                <CustomerAddressInvoicesSection
                  transactionAccountId={txaId}
                  invoiceFilterOptions={invoiceFilterOptions}
                  label={TAB_LABELS.REFUNDS}
                  shouldHideCustomiseView={true}
                  selectedInterfaceKey={refundedTabNameKey}
                />
              </TabPanel>
            )}
            {isDesignIteration1Enabled && (
              <TabPanel label={TAB_LABELS.DETAILS}>
                <section>{renderDetailsSummary()}</section>
              </TabPanel>
            )}
          </Tabs>
        </AppContent>
      </AppPage>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: 'Customers',
      href: routePaths.customerAddresses,
    },
    {
      title: heading,
      href: companyName ? routePaths.customerAddressDetailsByCompany : routePaths.customerAddressDetailsById,
    },
  ];

  const renderActions = () => {
    return !isDesignIteration1Enabled && soldToAddress ? (
      <CustomerAddressDetailPageActions
        transactionAccountId={txaId}
        soldToAddress={soldToAddress}
        refetch={refetch}
        isEditAddressDisabled={soldToAddress?.priceEligibility !== ShipToPriceEligibility.COMMERCIAL}
        {...(isDesignIteration1Enabled && {
          isADefaultButton: true,
        })}
      />
    ) : null;
  };

  const CustomerAddressDetailsSection = isDesignIteration1Enabled ? (
    <PageBackgroundV2
      heading={heading}
      actions={renderActions() ?? undefined}
      breadcrumbsProps={{ items: breadcrumbsItems }}
      className="customer-address-detail-v2"
      bottomBar={getPageHeading(true)}
    >
      {renderAppPage()}
    </PageBackgroundV2>
  ) : (
    <PageBackground
      heading={getPageHeading()}
      imageKey={Background.CUSTOMER_ADDRESS_DETAIL_BACKGROUND}
      className={classNames('page-background', 'customer-address-detail', {
        'grey-background': isTransparentBackground,
      })}
      isBackButtonEnabled={true}
      actions={renderActions()}
    >
      {renderAppPage()}
    </PageBackground>
  );

  return CustomerAddressDetailsSection;
};
